import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { BRAND } from "@/config/brand";
import { ArrowRight, ArrowLeft, Lock, Sparkles } from "lucide-react";
import { MfaCodeStep, MfaSetupStep } from "@/components/MfaSteps";
import { PasswordInput } from "@/components/PasswordInput";

export default function Login() {
  const { login, applyToken } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState(null); // {modo: "CODE"|"SETUP", token, email}

  const destino = () => {
    const n = new URLSearchParams(window.location.search).get("next");
    return n && n.startsWith("/") && !n.startsWith("//") ? n : "/admin";
  };
  const entrar = async (r) => {
    await applyToken(r.access_token, r.refresh_token);
    toast.success("¡Bienvenido!");
    nav(destino());
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await login(email, password);
      if (r?.mfa_required) { setMfa({ modo: "CODE", token: r.pending_token, email: r.email }); return; }
      if (r?.mfa_setup_required) { setMfa({ modo: "SETUP", token: r.setup_token, email: r.email }); return; }
      toast.success("¡Bienvenido!");
      nav(destino());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al ingresar");
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-gradient-to-br from-sky-500 via-sky-500 to-sky-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 grain-overlay opacity-40" />
        <Link to="/" className="relative flex items-center gap-2 font-display font-bold text-xl">
          <div className="w-10 h-10 grid place-items-center">
            <img src="/brand/aipos-logo.png" alt="Ai-POS" className="w-8 h-8" />
          </div>
          {BRAND.name}
        </Link>
        <div className="relative space-y-4">
          <h2 className="text-4xl font-bold font-display tracking-tight leading-tight">
            Vende. Controla. Entiende.
          </h2>
          <p className="text-sky-50/90 text-lg max-w-md">
            El POS moderno para PyMEs chilenas. En caja, online y autoservicio con un solo catálogo.
          </p>
        </div>
        <p className="relative text-sm text-sky-100/80">© {new Date().getFullYear()} {BRAND.name}</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" data-testid="login-back-btn" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6 font-medium">
            <ArrowLeft className="w-4 h-4" /> Volver al inicio
          </Link>
          <h1 className="text-3xl font-bold text-slate-900 font-display">
            {mfa ? (mfa.modo === "SETUP" ? "Seguridad de tu cuenta" : "Verificación en dos pasos") : "Ingresar"}
          </h1>
          <p className="mt-1 text-slate-600">
            {mfa ? "Un paso más y entras al panel." : "Bienvenido de vuelta."}
          </p>

          {mfa ? (
            <div className="mt-8">
              {mfa.modo === "CODE" ? (
                <MfaCodeStep pendingToken={mfa.token} email={mfa.email} onDone={entrar}
                             onCancel={() => { setMfa(null); setPassword(""); }} />
              ) : (
                <MfaSetupStep setupToken={mfa.token} email={mfa.email} onDone={entrar} />
              )}
            </div>
          ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" data-testid="auth-email-input" type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11 rounded-xl border-slate-200 focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
                placeholder="tu@empresa.cl"
              />
            </div>
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <PasswordInput
                id="password" data-testid="auth-password-input" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11 rounded-xl border-slate-200 focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
                placeholder="••••••••"
              />
              <div className="mt-1.5 text-right">
                <Link to="/recuperar-clave" data-testid="login-forgot-password-link" className="text-xs font-semibold text-sky-600 hover:text-sky-700">
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
            </div>

            <Button
              type="submit" data-testid="auth-submit-button" disabled={loading}
              className="w-full h-11 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-semibold shadow-sm"
            >
              {loading ? "Ingresando..." : (<>Ingresar <ArrowRight className="w-4 h-4 ml-1" /></>)}
            </Button>

            <Button
              type="button" data-testid="auth-register-cta" onClick={() => nav("/registro")} disabled={loading}
              variant="outline" className="w-full h-11 rounded-xl border-sky-200 text-sky-700 hover:bg-sky-50"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Crear cuenta · 30 días gratis
            </Button>

            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
              <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-slate-400">o</span></div>
            </div>

            <Button
              type="button" data-testid="auth-google-button" onClick={google}
              variant="outline" className="w-full h-11 rounded-xl border-slate-200 hover:bg-slate-50"
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Continuar con Google
            </Button>
          </form>
          )}

          {!mfa && (
          <>
          <p className="mt-6 text-sm text-slate-600 text-center">
            ¿No tienes cuenta? <Link to="/register" className="font-semibold text-sky-600 hover:text-sky-700">Regístrate gratis</Link>
          </p>
          <div className="mt-4 pt-4 border-t border-slate-100 text-center">
            <Link data-testid="login-pair-link" to="/pair" className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500 hover:text-sky-700">
              Emparejar este equipo con un código
            </Link>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
